import type { ReactElement } from 'react';
import React from 'react';
import Alert from 'atom-components/alert';
import SystemErrorAlert, { systemError } from 'feature-components/system-error-alert';
import MarkPolicy, {
  type AuthorisableMark,
  userIsActiveMarker,
  userIsAllocatedToResponse
} from 'policies/mark.policy';
import { hasMarkingAllocationRole } from 'policies/marking-allocation.policy';
import MarkingTaskPolicy, { hasMarkingTaskRole } from 'policies/marking-task.policy';
import type { MeCustomerUser } from 'services/assist-api/entities/me/me';
import {
  MarkStatus,
  type TestInstanceResponseMarkWithItems
} from 'services/assist-api/entities/test-instance-response-mark/types';
import type { TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import type { TestInstanceResponseWithResponseItemsAndMediaObjects } from 'services/assist-api/entities/test-instance-response/types';
import type { BaseTestInstance } from 'services/assist-api/entities/test-instance/types';
import type { TestRubricWithMediaObjects } from 'services/assist-api/entities/test-rubric/types';
import { usePageAccess } from 'services/providers/page-access.provider';
import InfoSection from './info-section';
import MarkResponseFormLoader from './mark-response-form-loader';

type Props = {
  assessment: BaseTestInstance;
  response: TestInstanceResponseWithResponseItemsAndMediaObjects;
  responseMarkers: TestInstanceResponseMarkerWithCustomerUser[];
  rubric: TestRubricWithMediaObjects;
  currentOrganisationUser: MeCustomerUser;
  mark?: TestInstanceResponseMarkWithItems;
};

function getCurrentUserMarker(
  responseMarkers: TestInstanceResponseMarkerWithCustomerUser[],
  currentOrganisationUser: MeCustomerUser
): TestInstanceResponseMarkerWithCustomerUser | undefined {
  return responseMarkers.find((m) => m.user.id === currentOrganisationUser.id);
}

export function getActiveMarker(
  responseMarkers: TestInstanceResponseMarkerWithCustomerUser[],
  currentOrganisationUser: MeCustomerUser,
  mark?: TestInstanceResponseMarkWithItems
): TestInstanceResponseMarkerWithCustomerUser | undefined {
  // We can consider the active marker to be the current user if there is no mark.
  return mark
    ? responseMarkers.find((m) => m.id === mark?.activeMarker?.id)
    : getCurrentUserMarker(responseMarkers, currentOrganisationUser);
}

function noActiveMarkerMessage(markExists: boolean): string {
  return `There is no active marker. ${
    markExists
      ? systemError('A mark should always have an active marker.')
      : 'A marking admin needs to assign a marker to the response in order for marking of it to be possible.'
  }`;
}

export default function MarkResponseAuthoriser({
  assessment,
  response,
  responseMarkers,
  rubric,
  currentOrganisationUser,
  mark
}: Props): ReactElement {
  const { authorisableUser } = usePageAccess();

  const currentUserMarker = getCurrentUserMarker(responseMarkers, currentOrganisationUser);
  const activeMarker = getActiveMarker(responseMarkers, currentOrganisationUser, mark);
  const authorisableMark: AuthorisableMark = {
    id: mark?.id,
    activeMarkerId: activeMarker?.id,
    status: mark?.status || MarkStatus.Assigned,
    responseMarkers
  };

  const isAllocatedToResponse = userIsAllocatedToResponse(authorisableUser, authorisableMark);
  const isActiveMarker = userIsActiveMarker(authorisableUser, authorisableMark);

  const canViewMarkForm = MarkPolicy.viewForm(authorisableUser, authorisableMark);
  const canEditMark = MarkPolicy.edit(authorisableUser, authorisableMark);
  const canAdministrateMark = MarkingTaskPolicy.administrate(authorisableUser);

  return (
    <>
      <InfoSection
        isAdministrator={canAdministrateMark}
        isActiveMarker={isActiveMarker}
        activeMarker={activeMarker}
        rubricMediaObjects={rubric?.mediaObjects || []}
        responseMediaObjects={response?.mediaObjects || []}
        markStatus={mark?.status ?? MarkStatus.Assigned}
        isAiMarked={mark?.aiMarked}
      />
      {hasMarkingAllocationRole(authorisableUser) && (
        <Alert variant="info">
          You are a marking (or other) admin. You can make changes to marks regardless of
          allocation. You will be automatically allocated to the response and set as the active
          marker when the form is saved.
        </Alert>
      )}
      {!isAllocatedToResponse && !hasMarkingAllocationRole(authorisableUser) && (
        <Alert variant="danger">
          You have not been been assigned as a marker of this response.
        </Alert>
      )}
      {!hasMarkingTaskRole(authorisableUser) && (
        <SystemErrorAlert>
          You have somehow reached this page without a marking task role.
        </SystemErrorAlert>
      )}
      {!activeMarker && !hasMarkingAllocationRole && (
        <Alert variant="danger">{noActiveMarkerMessage(mark !== undefined)}</Alert>
      )}
      {canViewMarkForm && (
        <MarkResponseFormLoader
          assessment={assessment}
          response={response}
          rubricId={rubric.id}
          canEditMark={canEditMark}
          canAdministrateMark={canAdministrateMark}
          currentOrganisationUser={currentOrganisationUser}
          currentUserMarker={currentUserMarker}
          activeMarker={activeMarker}
          mark={mark}
        />
      )}
    </>
  );
}
